/* ============================================================
   Configurator — live pricing, finish/setup/add-ons, checkout
   ============================================================ */
(function () {
  'use strict';

  if (window.__ddConfiguratorLoaded) return;
  window.__ddConfiguratorLoaded = true;
  const initializedRoots = new WeakSet();

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function initConfigurator(root) {
    if (initializedRoots.has(root)) return;
    const dataEl = $('[data-cfg-product-json]', root);
    if (!dataEl) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.warn('[configurator] could not parse product JSON', err);
      return;
    }

    initializedRoots.add(root);

    const variants = product.variants || [];
    const moneyFormat = root.dataset.moneyFormat || '€{{amount}}';
    const clearCart = root.dataset.clearCart === 'true';
    const purchaseMode = root.dataset.purchaseMode || 'direct';
    const directPurchase = purchaseMode === 'direct';
    const purchaseUrl = root.dataset.purchaseUrl || '';
    const purchaseLabel = root.dataset.purchaseLabel || 'Find a dealer';
    // The main product is sold in packs (e.g. a pair = 2 speakers), so its price
    // already covers `packSize` speakers. The setup's speaker count is divided by
    // this to work out how many units (pairs) to actually charge / add to cart.
    const packSize = Math.max(1, parseInt(root.dataset.packSize, 10) || 2);

    // Derive the store's number formatting (thousands/decimal separators, decimal
    // places) from a Liquid-rendered sample of 1234567 cents (= 12,345.67).
    const parseNumberFormat = (sample) => {
      const fallback = { thousands: ',', decimal: '.', decimals: 2 };
      if (!sample) return fallback;
      const core = (sample.match(/[\d.,'\u00a0 ]+/) || [''])[0].trim();
      const seps = core.replace(/\d/g, '');
      const unique = [];
      for (const ch of seps) if (!unique.includes(ch)) unique.push(ch);
      if (unique.length >= 2) {
        const decimal = unique[unique.length - 1];
        const lastIdx = core.lastIndexOf(decimal);
        return { thousands: unique[0], decimal: decimal, decimals: core.length - lastIdx - 1 };
      }
      if (unique.length === 1) {
        // Single separator on 12345.67 → store uses no decimals; it's the thousands sep.
        return { thousands: unique[0], decimal: '.', decimals: 0 };
      }
      return { thousands: '', decimal: '.', decimals: 0 };
    };

    const numFmt = parseNumberFormat(root.dataset.moneySample);

    const money = (cents) => {
      const fixed = (Number(cents) / 100).toFixed(numFmt.decimals);
      const parts = fixed.split('.');
      let intPart = parts[0];
      if (numFmt.thousands) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, numFmt.thousands);
      const out = numFmt.decimals > 0 ? intPart + numFmt.decimal + parts[1] : intPart;
      return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, out);
    };

    const imageEl = $('.cfg-visual-media img', root);
    const steps = $$('[data-cfg-step]', root);
    const initialSrc = imageEl ? imageEl.getAttribute('src') : '';
    const initialAlt = imageEl ? imageEl.getAttribute('alt') : '';
    let activeStep = null;

    // Per-variant image carousel for the sticky visual. galleryList holds the
    // images for the current variant (from its custom.gallery metafield, or its
    // featured image as a fallback). Only shown on steps without a step image.
    const galleryRoot = $('[data-cfg-gallery]', root);
    const galleryPrev = $('[data-cfg-gallery-prev]', root);
    const galleryNext = $('[data-cfg-gallery-next]', root);
    const galleryDots = $('[data-cfg-gallery-dots]', root);
    let galleryList = [];
    let galleryIndex = 0;
    let galleryActive = true;

    // Crossfade the sticky visual to a new image (preloads to avoid flashes).
    // The Liquid-rendered <img> ships with a srcset; the browser honours srcset
    // over src, so we strip srcset/sizes on the first swap to let src win.
    const swapImage = (src, alt) => {
      if (!imageEl || !src) return;
      if (imageEl.getAttribute('src') === src && !imageEl.getAttribute('srcset')) return;
      const pre = new Image();
      pre.addEventListener('load', () => {
        imageEl.removeAttribute('srcset');
        imageEl.removeAttribute('sizes');
        imageEl.src = src;
        if (alt != null) imageEl.alt = alt;
        imageEl.style.opacity = '';
      });
      imageEl.style.opacity = '0';
      pre.src = src;
    };

    const variantGallery = (v) => {
      if (v && Array.isArray(v.gallery) && v.gallery.length) return v.gallery;
      if (v && v.featured_image && v.featured_image.src) return [v.featured_image];
      if (initialSrc) return [{ src: initialSrc, alt: initialAlt }];
      return [];
    };

    const updateGalleryChrome = () => {
      const many = galleryActive && galleryList.length > 1;
      if (galleryPrev) galleryPrev.hidden = !many;
      if (galleryNext) galleryNext.hidden = !many;
      if (galleryDots) galleryDots.hidden = !many;
      if (galleryRoot) galleryRoot.classList.toggle('cfg-has-gallery', many);
      if (galleryDots) {
        $$('[data-cfg-gallery-dot]', galleryDots).forEach((d, i) => {
          d.setAttribute('aria-current', i === galleryIndex ? 'true' : 'false');
        });
      }
    };

    function showGalleryIndex(i) {
      if (!galleryList.length) return;
      const n = galleryList.length;
      galleryIndex = ((i % n) + n) % n;
      const img = galleryList[galleryIndex];
      if (img) swapImage(img.src, img.alt);
      updateGalleryChrome();
    }

    const renderDots = () => {
      if (!galleryDots) return;
      galleryDots.textContent = '';
      if (galleryList.length > 1) {
        const frag = document.createDocumentFragment();
        galleryList.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cfg-gallery-dot';
          dot.setAttribute('data-cfg-gallery-dot', '');
          dot.setAttribute('aria-label', 'View image ' + (i + 1));
          dot.addEventListener('click', () => showGalleryIndex(i));
          frag.appendChild(dot);
        });
        galleryDots.appendChild(frag);
      }
      updateGalleryChrome();
    };

    const setGalleryForVariant = (v) => {
      galleryList = variantGallery(v);
      galleryIndex = 0;
      renderDots();
    };

    const applyStepImage = (step) => {
      if (!step) return;
      // A step with its own image pins it (and hides the carousel); otherwise the
      // selected variant's gallery stays visible throughout the journey.
      if (step.dataset.stepImage) {
        galleryActive = false;
        updateGalleryChrome();
        swapImage(step.dataset.stepImage, step.dataset.stepAlt || '');
      } else {
        galleryActive = true;
        showGalleryIndex(galleryIndex);
      }
    };

    const optionGroups = $$('[data-cfg-option]', root);
    const unitPriceEl = $('[data-cfg-unit-price]', root);
    const setupRadios = $$('[data-cfg-setup]', root);
    const addonInputs = $$('[data-cfg-addon]', root);
    const addonQtySelects = $$('[data-cfg-addon-qty]', root);

    const cableContainers = $$('[data-cfg-cable]', root);

    const countEl = $('[data-cfg-summary-count]', root);
    const lineContainers = $$('[data-cfg-lines]', root);
    const checkoutBtns = $$('[data-cfg-checkout]', root);
    const summaryToggle = $('[data-cfg-summary-toggle]', root);
    const summaryDetails = $('[data-cfg-summary-details]', root);

    let currentVariant = variants.find((v) => v.id === product.selected_or_first_available_variant?.id)
      || variants.find((v) => v.available)
      || variants[0];

    // Sold-out-online state for the selected finish. When true, the checkout is
    // disabled and the restock-notification link is revealed (see refreshSoldOut).
    const restockEls = $$('[data-cfg-restock]', root);
    let soldOut = false;
    let wizardRefresh = null;

    const getSetupQty = () => {
      const active = setupRadios.find((r) => r.checked);
      return active ? Math.max(1, parseInt(active.dataset.qty, 10) || 1) : 1;
    };

    // Number of priced units (packs/pairs) to charge for the chosen setup.
    // e.g. Stereo (2 speakers) with a pair price = 1 unit. Odd counts round up
    // to whole packs so the customer always has enough speakers.
    const getUnitCount = () => Math.max(1, Math.ceil(getSetupQty() / packSize));

    const getSelectedAddons = () => {
      const out = [];
      addonInputs
        .filter((i) => i.checked)
        .forEach((i) => out.push({
          id: parseInt(i.dataset.variantId, 10),
          price: parseInt(i.dataset.price, 10) || 0,
          title: i.dataset.title || 'Add-on',
          qty: 1,
          pack: Math.max(1, parseInt(i.dataset.pack, 10) || 1),
        }));
      // Quantity add-ons (stands & mounts): contribute only when count > 0.
      addonQtySelects.forEach((sel) => {
        const qty = parseInt(sel.value, 10) || 0;
        if (qty > 0) out.push({
          id: parseInt(sel.dataset.variantId, 10),
          price: parseInt(sel.dataset.price, 10) || 0,
          title: sel.dataset.title || 'Add-on',
          qty: qty,
          pack: Math.max(1, parseInt(sel.dataset.pack, 10) || 1),
        });
      });
      return out.filter((a) => a.id);
    };

    // Rebuild the 0 → N options for each quantity add-on so N tracks the number
    // of speakers in the chosen setup, preserving (clamped) the current value.
    const syncAddonQtyRanges = () => {
      if (!addonQtySelects.length) return;
      const max = getSetupQty();
      addonQtySelects.forEach((sel) => {
        const current = Math.min(parseInt(sel.value, 10) || 0, max);
        const frag = document.createDocumentFragment();
        for (let i = 0; i <= max; i++) {
          const opt = document.createElement('option');
          opt.value = String(i);
          opt.textContent = String(i);
          if (i === current) opt.selected = true;
          frag.appendChild(opt);
        }
        sel.textContent = '';
        sel.appendChild(frag);
      });
    };

    const getSelectedCables = () => {
      const out = [];
      cableContainers.forEach((container) => {
        const name = container.dataset.cableName || 'Cable';
        $$('.cfg-cable-row', container).forEach((row) => {
          const sel = $('[data-cfg-cable-length]', row);
          const qtyEl = $('[data-cfg-cable-qty]', row);
          if (!sel || !sel.value) return;
          const opt = sel.options[sel.selectedIndex];
          if (!opt || opt.disabled) return;
          const qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
          out.push({
            id: parseInt(sel.value, 10),
            price: parseInt(opt.dataset.price, 10) || 0,
            length: opt.dataset.length || opt.textContent,
            name: name,
            qty: qty,
          });
        });
      });
      return out.filter((c) => c.id);
    };

    // ---- A/B experiment (cfg-wizard) + Umami event helpers ----------------
    // Variant is set pre-paint by snippets/experiment.liquid on <html>. Both
    // variants fire the SAME events so the comparison stays valid.
    const expVariant = document.documentElement.getAttribute('data-exp-cfg-wizard') === 'b' ? 'b' : 'a';
    const wizardMode = expVariant === 'b';
    // PARKED alternative first-step layouts — NOT part of the live A/B test.
    // The A/B test's variant "b" uses the colour swatches (default). These
    // image-card designs are kept in the theme for future use and are reachable
    // only by explicit URL, so real visitors never see them:
    //   ?finish=cards  → image cards beside the sticky hero image
    //   ?finish=hero   → cards ARE the visual (hero hidden, full-width big cards)
    const finishParam = (new URLSearchParams(window.location.search).get('finish') || '').toLowerCase();
    const finishHero = finishParam === 'hero';
    const finishCards = finishParam === 'cards' || finishHero;
    const productKey = root.dataset.product || '';
    const device = window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
    const track = (event, props) => {
      if (window.DD && typeof window.DD.track === 'function') {
        window.DD.track(event, Object.assign(
          { exp: 'cfg-wizard', variant: expVariant, product: productKey, device: device },
          props || {}
        ));
      }
    };
    const getTotalCents = () => {
      const unit = currentVariant ? currentVariant.price : 0;
      return unit * getUnitCount()
        + getSelectedAddons().reduce((s, a) => s + a.price * a.qty, 0)
        + getSelectedCables().reduce((s, c) => s + c.price * c.qty, 0);
    };
    const getAddonsCount = () => getSelectedAddons().length + getSelectedCables().length;
    let reviewTracked = false;
    const trackReachReview = () => {
      if (reviewTracked) return;
      reviewTracked = true;
      track('reach_review', { total: Math.round(getTotalCents() / 100), addons_count: getAddonsCount() });
    };

    const findVariant = () => {
      const selected = optionGroups.map((group) => {
        const active = $('[data-cfg-swatch][aria-pressed="true"]', group);
        return active ? active.dataset.value : null;
      });
      return variants.find((v) =>
        selected.every((val, i) => (val == null ? true : v.options[i] === val))
      );
    };

    const renderLines = () => {
    if (!lineContainers.length) return;
    const units = getUnitCount();
    // Show the physical speaker count in the line label (e.g. "× 2"), while the
    // price still reflects the number of priced pairs.
    const speakers = getSetupQty();
    const addons = getSelectedAddons();
      const cables = getSelectedCables();
      const unit = currentVariant ? currentVariant.price : 0;
      const mainTotal = unit * units;
      const total = mainTotal
        + addons.reduce((s, a) => s + a.price * a.qty, 0)
        + cables.reduce((s, c) => s + c.price * c.qty, 0);
      const finish = currentVariant && !product.has_only_default_variant
        ? ' · ' + currentVariant.options.join(' / ')
        : '';

      // Populate every line container (sticky summary + bottom order overview).
      lineContainers.forEach((el) => {
        el.textContent = '';
        const unitLabel = speakers === 1 ? '1 unit' : speakers + ' units';
      el.appendChild(buildRow(product.title + finish + ' (' + unitLabel + ')', money(mainTotal), false));
        addons.forEach((a) => {
          // Show the physical unit count (qty × pack) so a pair of stands reads
          // "(2 units)", matching the speaker line, while price stays per pack.
          const addonUnits = a.qty * (a.pack || 1);
          const label = addonUnits > 1 ? a.title + ' (' + addonUnits + ' units)' : a.title;
          el.appendChild(buildRow(label, money(a.price * a.qty), false));
        });
        cables.forEach((c) =>
          el.appendChild(buildRow(c.name + ' · ' + c.length + ' × ' + c.qty, money(c.price * c.qty), false))
        );
        el.appendChild(buildRow('Total', money(total), true));
      });
    };

    const buildRow = (name, amount, isTotal) => {
      const li = document.createElement('li');
      li.className = 'cfg-summary-line' + (isTotal ? ' cfg-summary-line--total' : '');
      const n = document.createElement('span');
      n.className = 'name';
      n.textContent = name;
      const a = document.createElement('span');
      a.className = 'amount';
      a.textContent = amount;
      li.append(n, a);
      return li;
    };

    const update = () => {
      const units = getUnitCount();
      const addons = getSelectedAddons();
      const cables = getSelectedCables();
      const unit = currentVariant ? currentVariant.price : 0;
      const total = unit * units
        + addons.reduce((s, a) => s + a.price * a.qty, 0)
        + cables.reduce((s, c) => s + c.price * c.qty, 0);
    // Count physical speakers (not priced pairs) so a stereo pair reads as
    // "2 items" rather than "1 item", matching what the customer receives.
    const itemCount = getSetupQty()
      + addons.reduce((s, a) => s + a.qty * (a.pack || 1), 0)
      + cables.reduce((s, c) => s + c.qty, 0);

      if (unitPriceEl && currentVariant) unitPriceEl.textContent = money(unit);
      // Query live so the wizard footer's total (added after init) also updates.
      $$('[data-cfg-total]', root).forEach((el) => { el.textContent = money(total); });
      if (countEl) countEl.textContent = itemCount === 1 ? '1 unit' : itemCount + ' units';
      renderLines();
    };

    const applyVariant = (variant) => {
      if (!variant) return;
      currentVariant = variant;
      setGalleryForVariant(variant);
      // Show the new finish's first image whenever the visual isn't pinned to a
      // dedicated step image.
      if (!activeStep || !activeStep.dataset.stepImage) {
        galleryActive = true;
        showGalleryIndex(0);
      }
      optionGroups.forEach((group) => {
        const out = $('[data-cfg-option-output]', group);
        const index = parseInt(group.dataset.optionIndex, 10);
        if (out && variant.options[index] != null) out.textContent = variant.options[index];
      });
      update();
      refreshSoldOut();
    };

    // Reveal the restock link and disable checkout when the chosen finish is not
    // available online. Scoped to the configurator only — nothing renders unless
    // the current finish is sold out.
    // QA override: ?soldout=1 forces the sold-out state on the current finish so
    // the restock UI can be verified without changing any inventory.
    const forceSoldOut = /(^|[?&])soldout=1(&|$)/.test(location.search);
    const refreshSoldOut = () => {
      soldOut = forceSoldOut || !!(currentVariant && currentVariant.available === false);
      restockEls.forEach((el) => { el.hidden = !directPurchase || !soldOut; });
      checkoutBtns.forEach((b) => {
        b.disabled = !directPurchase || soldOut;
        b.textContent = soldOut
          ? (b.dataset.labelSoldout || 'Sold out online')
          : (b.dataset.labelDefault || 'Checkout');
      });
      if (wizardRefresh) wizardRefresh();
    };

    // Finish swatches — selecting a colour updates the visual/price only, so
    // customers can freely compare colour after colour without the page moving.
    optionGroups.forEach((group) => {
      $$('[data-cfg-swatch]', group).forEach((sw) => {
        sw.addEventListener('click', () => {
          $$('[data-cfg-swatch]', group).forEach((s) => s.setAttribute('aria-pressed', 'false'));
          sw.setAttribute('aria-pressed', 'true');
          applyVariant(findVariant());
          track('cfg_select', { step: 'finish', option: sw.dataset.value || '' });
        });
      });
    });

    // Gallery navigation (arrows, dots wired in renderDots, swipe on mobile).
    if (galleryPrev) galleryPrev.addEventListener('click', () => showGalleryIndex(galleryIndex - 1));
    if (galleryNext) galleryNext.addEventListener('click', () => showGalleryIndex(galleryIndex + 1));
    if (galleryRoot) {
      let sx = 0, sy = 0, tracking = false;
      galleryRoot.addEventListener('touchstart', (e) => {
        if (!galleryActive || galleryList.length < 2) return;
        const t = e.changedTouches[0]; sx = t.clientX; sy = t.clientY; tracking = true;
      }, { passive: true });
      galleryRoot.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - sx;
        const dy = t.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          showGalleryIndex(galleryIndex + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    }

    setupRadios.forEach((r) => r.addEventListener('change', () => {
      syncAddonQtyRanges();
      update();
      const name = r.closest('.cfg-setup') ? (r.closest('.cfg-setup').querySelector('.cfg-setup-name') || {}).textContent : '';
      track('cfg_select', { step: 'setup', option: (name || r.value || '').trim() });
    }));
    addonInputs.forEach((i) => i.addEventListener('change', () => {
      update();
      track('cfg_select', { step: 'addon', option: i.dataset.title || '', selected: i.checked });
    }));
    addonQtySelects.forEach((sel) => sel.addEventListener('change', () => {
      update();
      track('cfg_select', { step: 'addon', option: sel.dataset.title || '', qty: parseInt(sel.value, 10) || 0 });
    }));

    // Cables — repeatable rows, each with a length + quantity
    const initCableRow = (row) => {
      const sel = $('[data-cfg-cable-length]', row);
      const qtyEl = $('[data-cfg-cable-qty]', row);
      const dec = $('[data-cfg-cable-dec]', row);
      const inc = $('[data-cfg-cable-inc]', row);
      const remove = $('[data-cfg-cable-remove]', row);
      const clampQty = () => {
        const n = parseInt(qtyEl.value, 10);
        qtyEl.value = !n || n < 1 ? 1 : n;
      };
      if (sel) sel.addEventListener('change', () => {
        update();
        const opt = sel.options[sel.selectedIndex];
        track('cfg_select', { step: 'cable', option: (opt && (opt.dataset.length || opt.textContent)) || '' });
      });
      if (qtyEl) qtyEl.addEventListener('input', () => { clampQty(); update(); });
      if (dec) dec.addEventListener('click', () => { qtyEl.value = Math.max(1, (parseInt(qtyEl.value, 10) || 1) - 1); update(); });
      if (inc) inc.addEventListener('click', () => { qtyEl.value = (parseInt(qtyEl.value, 10) || 1) + 1; update(); });
      if (remove) remove.addEventListener('click', () => { row.remove(); update(); });
    };

    cableContainers.forEach((container) => {
      const rowsEl = $('[data-cfg-cable-rows]', container);
      const tpl = $('[data-cfg-cable-template]', container);
      const addBtn = $('[data-cfg-cable-add]', container);
      if (!rowsEl || !tpl || !addBtn) return;
      addBtn.addEventListener('click', () => {
        const frag = tpl.content.cloneNode(true);
        const row = frag.querySelector('.cfg-cable-row');
        rowsEl.appendChild(frag);
        if (row) initCableRow(row);
        update();
        track('cfg_select', { step: 'cable', option: 'add', name: container.dataset.cableName || 'Cable' });
      });
    });

    if (summaryToggle && summaryDetails) {
      summaryToggle.addEventListener('click', () => {
        const open = summaryToggle.getAttribute('aria-expanded') === 'true';
        summaryToggle.setAttribute('aria-expanded', String(!open));
        summaryDetails.hidden = open;
      });
    }

    const doCheckout = async (btn) => {
      if (!directPurchase || !currentVariant || soldOut) return;
      const busy = btn.dataset.labelBusy || 'Adding…';
      checkoutBtns.forEach((b) => { b.disabled = true; });
      btn.textContent = busy;

      const items = [{ id: currentVariant.id, quantity: getUnitCount() }];
      getSelectedAddons().forEach((a) => items.push({ id: a.id, quantity: a.qty }));
      getSelectedCables().forEach((c) => items.push({ id: c.id, quantity: c.qty }));

      try {
        if (clearCart) {
          await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }
        // Primary conversion — fires for BOTH variants.
        track('add_to_cart', { total: Math.round(getTotalCents() / 100), addons_count: getAddonsCount() });
        window.location.href = '/checkout';
      } catch (err) {
        console.warn('[configurator] checkout failed', err);
        checkoutBtns.forEach((b) => { b.disabled = false; b.textContent = b.dataset.labelDefault || 'Checkout'; });
        alert('Sorry, we could not start checkout. Please try again.');
      }
    };

    checkoutBtns.forEach((btn) => btn.addEventListener('click', () => doCheckout(btn)));

    // Scroll-driven journey: the sticky visual follows whichever step sits at the
    // vertical centre of the viewport. Disabled in wizard mode, where navigation
    // (not scroll position) drives the active step.
    if (steps.length && !wizardMode) {
      activeStep = steps[0];
      let ticking = false;
      const syncStep = () => {
        ticking = false;
        const mid = window.innerHeight / 2;
        let best = null;
        for (const s of steps) {
          const r = s.getBoundingClientRect();
          if (r.top <= mid && r.bottom >= mid) { best = s; break; }
        }
        if (!best && window.scrollY < 120) best = steps[0];
        if (best && best !== activeStep) {
          activeStep = best;
          applyStepImage(best);
        }
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(syncStep);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      // Hide the sticky summary bar once the closing overview reaches the viewport
      // centre, so the page ends on a clean, top-aligned order card. We use an
      // IntersectionObserver (rather than the scroll handler) so it fires reliably
      // however the page is scrolled — wheel, keyboard, scrollbar or snap.
      const reviewStep = steps.find((s) => s.dataset.cfgStep === 'review');
      if (reviewStep && 'IntersectionObserver' in window) {
        // Hide the bar as soon as the review step rises above the sticky price
        // bar (~140px tall). Tying the trigger to the bar height — rather than a
        // viewport percentage — means it fires reliably at the end of the page
        // regardless of how tall the page or the review card is.
        new IntersectionObserver((entries) => {
          root.classList.toggle('cfg-at-review', entries[0].isIntersecting);
          if (entries[0].isIntersecting) trackReachReview();
        }, { rootMargin: '0px 0px -140px 0px' }).observe(reviewStep);
      }
    }

    // ---- Wizard mode (variant b): one step at a time, gated on choices -----
    function initWizardMode() {
      const panel = $('.cfg-panel', root);
      if (!panel || !steps.length) return;
      root.classList.add('cfg--wizard');

      const isRequired = (el) => el.dataset.cfgStep === 'finish' || el.dataset.cfgStep === 'setup';
      const isReviewEl = (el) => el.dataset.cfgStep === 'review';
      // Required steps start "unchosen" so the customer must actively pick to advance.
      const chosen = steps.map((el) => !isRequired(el));
      // Safety: if the finish step has no selectable swatches (single-variant
      // product), don't trap the customer on a gate they can't satisfy.
      if (!optionGroups.length || product.has_only_default_variant) {
        steps.forEach((el, i) => { if (el.dataset.cfgStep === 'finish') chosen[i] = true; });
      }
      let idx = 0;

      const progress = document.createElement('div');
      progress.className = 'cfg-wiz-progress';
      steps.forEach(() => {
        const seg = document.createElement('span');
        seg.className = 'cfg-wiz-seg';
        progress.appendChild(seg);
      });
      panel.insertBefore(progress, panel.firstChild);

      // Full-width footer that REPLACES the sticky summary bar in wizard mode:
      // running total on the left, navigation on the right (one bottom bar, no
      // overlap). CSS hides .cfg-summary while .cfg--wizard is set.
      const nav = document.createElement('div');
      nav.className = 'cfg-wiz-nav';
      const totalWrap = document.createElement('div');
      totalWrap.className = 'cfg-wiz-total';
      totalWrap.innerHTML = '<span class="cfg-wiz-total-label">Total</span> <span data-cfg-total></span>';
      const actions = document.createElement('div');
      actions.className = 'cfg-wiz-actions';
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'btn btn-outline cfg-wiz-back';
      backBtn.textContent = 'Back';
      const skipBtn = document.createElement('button');
      skipBtn.type = 'button';
      skipBtn.className = 'cfg-wiz-skip';
      skipBtn.textContent = 'Skip';
      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'btn btn-accent cfg-wiz-next';
      // Label lives in its own span so the trailing arrow icon survives the
      // label swaps in render() (Continue ⇄ Checkout).
      const nextLabel = document.createElement('span');
      nextLabel.className = 'cfg-wiz-next-label';
      nextLabel.textContent = 'Continue';
      nextBtn.appendChild(nextLabel);
      nextBtn.insertAdjacentHTML('beforeend',
        '<svg class="ic cfg-wiz-next-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M13 5l7 7-7 7"/></svg>');
      const purchaseLink = document.createElement('a');
      purchaseLink.className = 'btn btn-accent cfg-wiz-purchase-link';
      purchaseLink.textContent = purchaseLabel;
      if (purchaseUrl) purchaseLink.href = purchaseUrl;
      const purchaseMessage = document.createElement('p');
      purchaseMessage.className = 'cfg-purchase-message cfg-wiz-purchase-message';
      purchaseMessage.textContent = root.dataset.purchaseMessage || '';
      actions.append(skipBtn, backBtn, nextBtn, purchaseLink);
      nav.appendChild(purchaseMessage);
      nav.append(totalWrap, actions);
      root.appendChild(nav);

      const segs = Array.from(progress.children);
      const canAdvance = () => chosen[idx];

      const render = () => {
        steps.forEach((el, n) => { el.toggleAttribute('data-cfg-hidden', n !== idx); });
        segs.forEach((s, n) => {
          s.classList.toggle('is-done', n < idx);
          s.classList.toggle('is-active', n === idx);
        });
        const el = steps[idx];
        const review = isReviewEl(el);
        const optional = !isRequired(el) && !review;
        backBtn.hidden = idx === 0;
        nextLabel.textContent = review
          ? (soldOut ? (nextBtn.dataset.labelSoldout || 'Sold out online') : (nextBtn.dataset.checkoutLabel || 'Checkout'))
          : 'Continue';
        // Sold-out finish blocks the whole journey: can't continue past it and
        // can't check out at review.
        nextBtn.disabled = (directPurchase && soldOut) || (!review && !canAdvance());
        nextBtn.hidden = review && !directPurchase;
        purchaseLink.hidden = !review || purchaseMode !== 'link' || !purchaseUrl;
        purchaseMessage.hidden = !review || purchaseMode !== 'link' || !purchaseMessage.textContent;
        skipBtn.hidden = !optional;
        // Cards-as-visual mode: drop the sticky hero on the finish step (the
        // cards are the imagery); it returns on later steps showing the pick.
        if (finishHero) root.classList.toggle('cfg-hide-visual', el.dataset.cfgStep === 'finish');
        activeStep = el;
        applyStepImage(el);
        if (review) trackReachReview();
      };

      const go = (n) => {
        idx = Math.max(0, Math.min(steps.length - 1, n));
        render();
        const top = root.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
      };

      // Grab the review step's own checkout label so the footer button matches.
      const reviewStepEl = steps.find(isReviewEl);
      const reviewCheckoutBtn = reviewStepEl ? $('[data-cfg-checkout]', reviewStepEl) : null;
      if (reviewCheckoutBtn) {
        nextBtn.dataset.checkoutLabel = reviewCheckoutBtn.dataset.labelDefault || 'Checkout';
        nextBtn.dataset.labelSoldout = reviewCheckoutBtn.dataset.labelSoldout || 'Sold out online';
      }

      // Let refreshSoldOut re-run this step's render so the footer button reflects
      // availability the moment a sold-out finish is picked.
      wizardRefresh = render;

      nextBtn.addEventListener('click', () => {
        if (directPurchase && soldOut) return;
        if (isReviewEl(steps[idx])) {
          if (directPurchase && reviewCheckoutBtn) reviewCheckoutBtn.click();
          return;
        }
        if (canAdvance()) go(idx + 1);
      });
      backBtn.addEventListener('click', () => go(idx - 1));
      skipBtn.addEventListener('click', () => go(idx + 1));

      const markChosen = (stepName) => {
        const i = steps.findIndex((s) => s.dataset.cfgStep === stepName);
        if (i < 0) return;
        chosen[i] = true;
        // Don't re-enable Continue for a sold-out finish (refreshSoldOut, which
        // runs on the same swatch click, has already disabled it).
        if (i === idx && (!directPurchase || !soldOut)) nextBtn.disabled = false;
      };
      optionGroups.forEach((group) => {
        $$('[data-cfg-swatch]', group).forEach((sw) => sw.addEventListener('click', () => markChosen('finish')));
      });
      setupRadios.forEach((r) => r.addEventListener('change', () => markChosen('setup')));

      // Alternative first-step layout (opt-in via ?finish=cards): show each
      // finish as a large image card side by side instead of colour dots. The
      // original swatches stay in the DOM (hidden) and drive all state — a card
      // click just proxies to its swatch, so pricing/gating/tracking are shared.
      if (finishCards) initFinishCards();

      render();
    }

    // Build the image-card finish picker over the existing swatches.
    function initFinishCards() {
      const finishStep = steps.find((s) => s.dataset.cfgStep === 'finish');
      if (!finishStep) return;
      const sws = $$('[data-cfg-swatch]', finishStep);
      if (!sws.length) return;
      finishStep.classList.add('cfg-finish--cards');

      const variantForValue = (val) =>
        variants.find((v) => v.options && v.options[0] === val);

      const grid = document.createElement('div');
      grid.className = 'cfg-finish-cards' + (finishHero ? ' cfg-finish-cards--hero' : '');

      const cards = sws.map((sw) => {
        const val = sw.dataset.value || '';
        const v = variantForValue(val);
        const src = (v && v.featured_image && v.featured_image.src) || initialSrc || '';

        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'cfg-finish-card';
        card.setAttribute('aria-pressed', sw.getAttribute('aria-pressed') || 'false');

        const media = document.createElement('span');
        media.className = 'cfg-finish-card-media';
        if (src) {
          const img = document.createElement('img');
          img.src = src;
          img.alt = val;
          img.loading = 'lazy';
          media.appendChild(img);
        }

        const info = document.createElement('span');
        info.className = 'cfg-finish-card-info';
        const name = document.createElement('span');
        name.className = 'cfg-finish-card-name';
        name.textContent = val;
        info.appendChild(name);
        if (v) {
          const price = document.createElement('span');
          price.className = 'cfg-finish-card-price';
          price.textContent = money(v.price);
          info.appendChild(price);
        }

        card.append(media, info);
        card.addEventListener('click', () => {
          sw.click();
          cards.forEach((c) => c.setAttribute('aria-pressed', 'false'));
          card.setAttribute('aria-pressed', 'true');
        });
        grid.appendChild(card);
        return card;
      });

      // Place the cards where the swatches were; the swatch fieldset is hidden
      // by CSS (.cfg-finish--cards .cfg-options) but stays live for state.
      const opts = $('.cfg-options', finishStep);
      if (opts) opts.parentNode.insertBefore(grid, opts.nextSibling);
      else finishStep.appendChild(grid);
    }

    if (wizardMode) initWizardMode();

    syncAddonQtyRanges();
    applyVariant(currentVariant);
    track('configurator_view', {});
  }

  function boot(root = document) {
    $$('[data-configurator]', root).forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
